/**
 * Camera Capture Component
 *
 * A mobile-optimized camera capture component for receipt photos.
 * Features:
 * - Camera viewfinder using getUserMedia API
 * - Rear camera preference (facingMode: 'environment')
 * - High resolution capture (1920x1080 ideal)
 * - Photo preview with retake/confirm options
 * - File input fallback when camera unavailable
 * - Permission handling with graceful fallback
 * - Glass morphism styling for dark theme
 * - File size validation (max 20MB)
 *
 * Browser Requirements:
 * - HTTPS required (except localhost)
 * - User gesture required to start camera on iOS
 */

import { renderIcon } from '../utils/icons';

/**
 * Maximum file size for uploaded images (20MB)
 */
const MAX_FILE_SIZE = 20 * 1024 * 1024;

/**
 * Allowed image MIME types
 */
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Camera capture component options
 */
export interface CameraCaptureOptions {
  /** Callback when photo is captured and confirmed */
  onCapture: (file: File) => void;
  /** Callback when capture is cancelled */
  onCancel: () => void;
  /** JPEG quality (0-1), default 0.85 */
  quality?: number;
}

/**
 * Camera availability check result
 */
export interface CameraAvailabilityResult {
  /** Whether camera is available */
  available: boolean;
  /** Error message if not available */
  error?: string;
}

/**
 * Check if camera is available on this device
 */
export async function checkCameraAvailability(): Promise<CameraAvailabilityResult> {
  // Check if getUserMedia is supported
  if (!navigator.mediaDevices?.getUserMedia) {
    return { available: false, error: 'Camera API not supported' };
  }

  // Check for HTTPS (required for getUserMedia)
  if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
    return { available: false, error: 'HTTPS required for camera access' };
  }

  // Check permission status if available
  try {
    const permission = await navigator.permissions.query({
      name: 'camera' as PermissionName,
    });
    if (permission.state === 'denied') {
      return { available: false, error: 'Camera permission denied' };
    }
  } catch {
    // permissions.query may not support 'camera' in all browsers
  }

  return { available: true };
}

/**
 * Convert a data URL to a File object
 */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0]?.match(/:(.*?);/);
  const mime = mimeMatch?.[1] ?? 'image/jpeg';
  const base64Data = arr[1] ?? '';
  const bstr = atob(base64Data);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

/**
 * Camera Capture Component
 */
export class CameraCapture {
  private modal: HTMLDivElement | null = null;
  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private options: Required<CameraCaptureOptions>;
  private capturedDataUrl: string | null = null;

  constructor(options: CameraCaptureOptions) {
    this.options = {
      quality: 0.85,
      ...options,
    };
  }

  /**
   * Open the camera capture modal
   */
  public async open(): Promise<void> {
    this.modal = this.createModal();
    document.body.appendChild(this.modal);

    // Try to initialize camera
    try {
      await this.initializeCamera();
    } catch (error) {
      // Show fallback UI
      this.showFallback(
        error instanceof Error ? error.message : 'Camera not available'
      );
    }
  }

  /**
   * Close the camera capture modal
   */
  public close(): void {
    this.stopCamera();
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
    this.capturedDataUrl = null;
  }

  /**
   * Create the modal DOM structure
   */
  private createModal(): HTMLDivElement {
    const modal = document.createElement('div');
    modal.className =
      'camera-capture-modal fixed inset-0 z-50 bg-neutral-950/95 flex flex-col';

    // Header
    const header = document.createElement('div');
    header.className = 'flex items-center justify-between p-4';

    const title = document.createElement('h2');
    title.className = 'text-foreground font-medium';
    title.textContent = 'Capture Receipt';
    header.appendChild(title);

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className =
      'camera-cancel-btn p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-neutral-800 transition-colors';
    cancelBtn.appendChild(renderIcon('x', { size: 'md' }));
    cancelBtn.addEventListener('click', () => {
      this.close();
      this.options.onCancel();
    });
    header.appendChild(cancelBtn);

    modal.appendChild(header);

    // Viewfinder container
    const viewfinder = document.createElement('div');
    viewfinder.className =
      'camera-viewfinder flex-1 min-h-0 flex items-center justify-center p-4 overflow-hidden';

    // Video element for camera stream
    this.video = document.createElement('video');
    this.video.className = 'max-w-full max-h-full rounded-lg bg-neutral-900 object-contain';
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('autoplay', '');
    this.video.setAttribute('muted', '');
    viewfinder.appendChild(this.video);

    modal.appendChild(viewfinder);

    // Controls
    const controls = document.createElement('div');
    controls.className = 'camera-controls p-6 flex justify-center';

    const captureBtn = document.createElement('button');
    captureBtn.type = 'button';
    captureBtn.className =
      'camera-capture-btn w-16 h-16 rounded-full bg-white border-4 border-primary flex items-center justify-center hover:scale-105 transition-transform';
    captureBtn.appendChild(
      renderIcon('camera', { size: 'lg', className: 'text-neutral-900' })
    );
    captureBtn.addEventListener('click', () => this.capturePhoto());
    controls.appendChild(captureBtn);

    modal.appendChild(controls);

    return modal;
  }

  /**
   * Initialize the camera stream
   */
  private async initializeCamera(): Promise<void> {
    const constraints: MediaStreamConstraints = {
      video: {
        facingMode: 'environment', // Rear camera
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (this.video) {
        this.video.srcObject = this.stream;
        await this.video.play();
      }
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          throw new Error('Camera permission denied');
        }
        if (error.name === 'NotFoundError') {
          throw new Error('No camera found');
        }
      }
      throw error;
    }
  }

  /**
   * Stop the camera stream
   */
  private stopCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.video) {
      this.video.srcObject = null;
    }
  }

  /**
   * Capture a photo from the video stream
   */
  private capturePhoto(): void {
    if (!this.video) return;

    // Create canvas and capture frame
    const canvas = document.createElement('canvas');
    canvas.width = this.video.videoWidth || 1920;
    canvas.height = this.video.videoHeight || 1080;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(this.video, 0, 0);

    // Convert to data URL
    this.capturedDataUrl = canvas.toDataURL('image/jpeg', this.options.quality);

    // Stop camera and show preview
    this.stopCamera();
    this.showPreview();
  }

  /**
   * Show the captured photo preview
   */
  private showPreview(): void {
    if (!this.modal || !this.capturedDataUrl) return;

    // Update title
    const title = this.modal.querySelector('h2');
    if (title) {
      title.textContent = 'Preview';
    }

    // Replace viewfinder with preview
    const viewfinder = this.modal.querySelector('.camera-viewfinder');
    if (viewfinder) {
      viewfinder.innerHTML = '';
      viewfinder.className =
        'camera-preview flex-1 min-h-0 flex items-center justify-center p-4 overflow-hidden';

      const img = document.createElement('img');
      img.src = this.capturedDataUrl;
      img.className = 'max-w-full max-h-full rounded-lg object-contain';
      img.alt = 'Captured receipt preview';
      viewfinder.appendChild(img);
    }

    // Replace controls with preview actions
    const controls = this.modal.querySelector('.camera-controls');
    if (controls) {
      controls.innerHTML = '';
      controls.className = 'camera-preview-controls p-6 flex justify-center gap-4';

      // Retake button
      const retakeBtn = document.createElement('button');
      retakeBtn.type = 'button';
      retakeBtn.className =
        'camera-retake-btn px-6 py-3 bg-neutral-800 text-foreground rounded-lg hover:bg-neutral-700 transition-colors font-medium';
      retakeBtn.textContent = 'Retake';
      retakeBtn.addEventListener('click', () => this.retake());
      controls.appendChild(retakeBtn);

      // Use Photo button
      const useBtn = document.createElement('button');
      useBtn.type = 'button';
      useBtn.className =
        'camera-use-btn px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-accent-600 transition-colors font-medium';
      useBtn.textContent = 'Use Photo';
      useBtn.addEventListener('click', () => this.confirmCapture());
      controls.appendChild(useBtn);
    }
  }

  /**
   * Retake the photo - return to viewfinder
   */
  private async retake(): Promise<void> {
    this.capturedDataUrl = null;

    if (!this.modal) return;

    // Reset title
    const title = this.modal.querySelector('h2');
    if (title) {
      title.textContent = 'Capture Receipt';
    }

    // Restore viewfinder
    const preview = this.modal.querySelector('.camera-preview');
    if (preview) {
      preview.innerHTML = '';
      preview.className =
        'camera-viewfinder flex-1 min-h-0 flex items-center justify-center p-4 overflow-hidden';

      this.video = document.createElement('video');
      this.video.className = 'max-w-full max-h-full rounded-lg bg-neutral-900 object-contain';
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('autoplay', '');
      this.video.setAttribute('muted', '');
      preview.appendChild(this.video);
    }

    // Restore controls
    const controls = this.modal.querySelector('.camera-preview-controls');
    if (controls) {
      controls.innerHTML = '';
      controls.className = 'camera-controls p-6 flex justify-center';

      const captureBtn = document.createElement('button');
      captureBtn.type = 'button';
      captureBtn.className =
        'camera-capture-btn w-16 h-16 rounded-full bg-white border-4 border-primary flex items-center justify-center hover:scale-105 transition-transform';
      captureBtn.appendChild(
        renderIcon('camera', { size: 'lg', className: 'text-neutral-900' })
      );
      captureBtn.addEventListener('click', () => this.capturePhoto());
      controls.appendChild(captureBtn);
    }

    // Reinitialize camera
    try {
      await this.initializeCamera();
    } catch (error) {
      this.showFallback(
        error instanceof Error ? error.message : 'Camera not available'
      );
    }
  }

  /**
   * Confirm the captured photo
   */
  private confirmCapture(): void {
    if (!this.capturedDataUrl) return;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `receipt-${timestamp}.jpg`;

    // Convert to File and call callback
    const file = dataUrlToFile(this.capturedDataUrl, filename);
    this.close();
    this.options.onCapture(file);
  }

  /**
   * Show fallback UI when camera is unavailable
   */
  private showFallback(errorMessage: string): void {
    if (!this.modal) return;

    // Replace viewfinder with fallback
    const viewfinder = this.modal.querySelector('.camera-viewfinder');
    if (viewfinder) {
      viewfinder.innerHTML = '';
      viewfinder.className =
        'camera-fallback flex-1 min-h-0 flex flex-col items-center justify-center p-8 overflow-hidden';

      // Icon
      const iconWrapper = document.createElement('div');
      iconWrapper.className =
        'w-16 h-16 rounded-full bg-neutral-800 flex items-center justify-center mb-4';
      iconWrapper.appendChild(
        renderIcon('camera', { size: 'xl', className: 'text-muted-foreground' })
      );
      viewfinder.appendChild(iconWrapper);

      // Error message
      const title = document.createElement('p');
      title.className = 'text-foreground font-medium mb-2';
      title.textContent = 'Camera not available';
      viewfinder.appendChild(title);

      const description = document.createElement('p');
      description.className =
        'text-muted-foreground text-sm text-center max-w-xs mb-6';
      description.textContent =
        errorMessage ||
        'Please select an image from your photo gallery instead.';
      viewfinder.appendChild(description);

      // File input button
      const label = document.createElement('label');
      label.className =
        'inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-accent-600 transition-colors cursor-pointer';
      label.appendChild(renderIcon('upload', { size: 'sm' }));

      const span = document.createElement('span');
      span.textContent = 'Select Image';
      label.appendChild(span);

      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.setAttribute('capture', 'environment');
      input.className = 'hidden';
      input.addEventListener('change', (e) => this.handleFileSelect(e));
      label.appendChild(input);

      viewfinder.appendChild(label);
    }

    // Hide capture controls
    const controls = this.modal.querySelector('.camera-controls');
    if (controls) {
      controls.remove();
    }
  }

  /**
   * Handle file selection from fallback input
   */
  private handleFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      this.showFallbackError(`File too large. Maximum size is ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB.`);
      input.value = '';
      return;
    }

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      this.showFallbackError('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
      input.value = '';
      return;
    }

    // Read file as data URL for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      this.capturedDataUrl = e.target?.result as string;
      this.showPreview();
    };
    reader.onerror = () => {
      this.showFallbackError('Failed to read file. Please try again.');
      input.value = '';
    };
    reader.readAsDataURL(file);
  }

  /**
   * Show error message in fallback UI
   */
  private showFallbackError(message: string): void {
    if (!this.modal) return;

    const fallback = this.modal.querySelector('.camera-fallback');
    if (!fallback) return;

    // Find or create error message element
    let errorEl = fallback.querySelector('.camera-fallback-error') as HTMLParagraphElement;
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'camera-fallback-error text-destructive text-sm text-center mt-4';
      fallback.appendChild(errorEl);
    }
    errorEl.textContent = message;

    // Clear error after 5 seconds
    setTimeout(() => {
      if (errorEl.textContent === message) {
        errorEl.textContent = '';
      }
    }, 5000);
  }
}

/**
 * Factory function to create a camera capture component
 *
 * @param options - Configuration options for the camera capture
 * @returns A new CameraCapture instance
 *
 * @example
 * ```typescript
 * const camera = createCameraCapture({
 *   onCapture: (file) => uploadReceipt(file),
 *   onCancel: () => console.log('Cancelled'),
 *   quality: 0.85,
 * });
 * await camera.open();
 * ```
 */
export function createCameraCapture(
  options: CameraCaptureOptions
): CameraCapture {
  return new CameraCapture(options);
}
